import type {
  CellClassFunc,
  CellRendererSelectorFunc,
  ColDef,
  ColGroupDef,
  ValueFormatterFunc,
  ValueGetterFunc,
} from 'ag-grid-community';
import { compact, get } from 'lodash';
import type { Scenario } from 'venn-api';
import { assertNotNil, Dates, getComparisonLabelForBlockLegend } from 'venn-utils';
import { BOLD_CLASS, ITALIC_CLASS, LEFT_ALIGN_CLASS } from '../customAnalysisContants';
import { isReturnsGridRow, isScenarioRow } from './typeUtils';
import ValueCellRenderer from '../components/grid/renderers/ValueCellRenderer';
import { getDefaultCellClass, getDefaultValueFormat, getSpecialClass } from './gridStyling';
import { BasicHeaderRenderer } from '../components/grid/renderers/BasicHeaderRenderer';
import HeaderCellRenderer from '../components/grid/renderers/HeaderCellRenderer';
import { getGroupColDef } from './getGroupColDef';
import type { ColumnParser, CreateLabelColumnProps, HeaderComponentParamsType, ScenarioRowData } from '../types';
import { formatData } from '../../data-grid';
import ScenarioHeaderCellRenderer from '../components/grid/renderers/ScenarioHeaderCellRenderer';
import ScenarioHeaderGroupCellRenderer from '../components/grid/renderers/ScenarioHeaderGroupCellRenderer';
import ScenarioCombinedCellRenderer from '../components/grid/renderers/ScenarioCombinedCellRenderer';
import { convertByUnits, getUnitPrecision, getUnitSymbol } from '../../utils/scenario';
import ScenarioIndexCellRenderer from '../components/grid/renderers/ScenarioIndexCellRenderer';
import { formatExportableSubjectWithOptionalFee } from '../../legend';
import { measureGridText, measureHeader } from '../../utils';
import { type StudioAnalysisRequest } from 'venn-state';

// TODO: split this file into multiple files, it is getting too long and complicated.

export const metricsSettingsColumnParser: ColumnParser = ({
  requests,
  analysesGroup,
  metricsSettings,
  theme,
  selectedBlock,
  gridStyle,
  skipKSuffixInFormatting,
  getRequestSubjectFee,
}) => {
  const labelColumn = createLabelColumn({
    requests,
  });

  const groupColumns = getGroupColDef(
    requests,
    analysesGroup,
    metricsSettings,
    theme,
    gridStyle,
    selectedBlock?.relativeToBenchmark,
    selectedBlock?.contributionToPercentage,
    selectedBlock?.selectedMetrics,
    selectedBlock?.selectedFactors,
    skipKSuffixInFormatting,
    getRequestSubjectFee,
  );

  return [getGroupColumnLabel(labelColumn), ...groupColumns];
};

const getFormattedScenarioShock = (scenario: Scenario) => {
  const sign = scenario.shock > 0 ? '+' : '';
  const shockValue = parseFloat(convertByUnits(scenario.shock, scenario.units)).toFixed(
    getUnitPrecision(scenario.units),
  );
  const unitSymbol = getUnitSymbol(scenario.units);
  return `${sign}${shockValue} ${unitSymbol}`;
};

const scenarioCellRendererSelector: CellRendererSelectorFunc<ScenarioRowData> = ({ data }) => ({
  component: data && isScenarioRow(data) ? ScenarioCombinedCellRenderer : ValueCellRenderer,
});
const scenarioValueFormatter: ValueFormatterFunc<ScenarioRowData> = ({ value }) =>
  formatData({ num: value, dataType: 'PERCENTAGE', precision: 1 });
const scenarioLabelCellRendererSelector: CellRendererSelectorFunc<ScenarioRowData> = ({ data }) => ({
  component: data && isScenarioRow(data) ? ScenarioIndexCellRenderer : ValueCellRenderer,
});

const scenarioValueGetter: ValueGetterFunc<ScenarioRowData> = ({ data, colDef }) => {
  const index = Number(colDef.field);
  const predicted = data?.scenarioAnalysis?.[index]?.predicted;
  return predicted && !Number.isNaN(predicted) ? predicted : '--';
};
const scenarioReturnValueGetter: ValueGetterFunc<ScenarioRowData> = ({ data }) => {
  const scenario = data?.scenario;
  return scenario ? getFormattedScenarioShock(scenario) : '--';
};
const scenarioReturnValueFormatter: ValueFormatterFunc<ScenarioRowData> = ({ data }) => {
  const scenario = data?.scenario;
  return scenario ? getFormattedScenarioShock(scenario) : '--';
};

const scenarioLabelColumnCellParams = { style: { paddingLeft: '5px', textAlign: 'left', width: '100%' } };

const getAllShockStrings = (request: StudioAnalysisRequest | undefined) =>
  request?.scenarios?.map(getFormattedScenarioShock) ?? [];

export const scenarioColumnParser: ColumnParser<ScenarioRowData> = ({
  requests,
  theme,
  gridStyle,
  getRequestSubjectFee,
}) => {
  const scenarioReturn = {
    headerName: 'Shock Value',
    headerComponent: ScenarioHeaderCellRenderer,
    valueGetter: scenarioReturnValueGetter,
    valueFormatter: scenarioReturnValueFormatter,
    cellClass: getDefaultCellClass,
    resizable: false,
    minWidth: Math.max(
      ...getAllShockStrings(requests?.[0]).map((shock) => measureGridText(shock, 'normal', gridStyle, theme)),
      measureHeader('Shock Value', theme, gridStyle),
    ),
  };

  const labelColumn = getGroupColumnLabel({
    ...createLabelColumn({
      requests,
    }),
    headerName: 'Input',
    minWidth: 200,
    maxWidth: undefined,
    headerComponent: BasicHeaderRenderer,
    cellRendererParams: scenarioLabelColumnCellParams,
    headerComponentParams: {
      style: { textAlign: 'left', width: '100%', color: theme.Colors.Black },
    },
    cellRendererSelector: scenarioLabelCellRendererSelector,
    resizable: false,
  });
  labelColumn.children.push(scenarioReturn);

  const columns = requests?.map<ColDef>((req, index) => {
    const displayName = getComparisonLabelForBlockLegend(req.subject);
    const headerParams = {
      isScenarioSubjectsHeaderGroup: true,
      displayName,
      subject: req.subject,
      relativeTo: req.relative && req.benchmarkType === 'INDIVIDUAL' ? req.benchmark : undefined,
      isCommonBenchmark: req.isBenchmark,
      style: {
        borderTop: 'none',
      },
    };

    return {
      headerName: `${formatExportableSubjectWithOptionalFee(req.subject, false, getRequestSubjectFee?.({ subject: req.subject, isBenchmark: req.isBenchmark }))}${
        req.benchmark && req.relative && req.benchmarkType === 'INDIVIDUAL' ? ` relative to ${req.benchmark.name}` : ''
      }`,
      resizable: false,
      isScenarioSubjectsHeaderGroup: true,
      headerGroupComponent: HeaderCellRenderer,
      headerComponent: HeaderCellRenderer,
      headerComponentParams: headerParams,
      headerGroupComponentParams: headerParams,
      field: String(index),
      valueGetter: scenarioValueGetter,
      valueFormatter: scenarioValueFormatter,
      cellRendererSelector: scenarioCellRendererSelector,
      minWidth: Math.max(
        measureGridText('888.8% ± 88.8', 'normal', gridStyle, theme),
        measureHeader(headerParams.displayName, theme, gridStyle),
      ),
      cellRendererParams: { subjectName: requests[index]!.subject.name, index, relative: requests[index]!.relative },
    };
  });

  const parentName = requests?.[0]!.relative ? 'Estimated Relative Return' : 'Estimated Return';

  const columnsMinWidthSum = columns.reduce((acc, col) => acc + (col.minWidth ?? 0), 0);
  const groupMinWidth = measureGridText(parentName, 'bold', gridStyle, theme);
  if (groupMinWidth > columnsMinWidthSum && columns.length > 0) {
    const column = assertNotNil(columns[0]);
    column.minWidth = (column.minWidth ?? 0) + groupMinWidth - columnsMinWidthSum;
  }

  const parentColumn: ColGroupDef = {
    marryChildren: true,
    headerName: parentName,
    headerGroupComponent: ScenarioHeaderGroupCellRenderer,
    headerGroupComponentParams: { style: { height: '20px', borderBottom: 'none' } },
    children: columns,
  };

  return [labelColumn, parentColumn];
};

export const defaultColumnParser: ColumnParser = ({ requests, getRequestSubjectFee }) => {
  const labelColumn = createLabelColumn({
    requests,
  });

  const columns: ColDef[] = requests.map((req, index) => {
    const headerName = formatExportableSubjectWithOptionalFee(
      req.subject,
      false,
      getRequestSubjectFee?.({ subject: req.subject, isBenchmark: req.isBenchmark }),
    );
    const headerComponentParams: HeaderComponentParamsType = {
      subject: req.subject,
      isCommonBenchmark: req.isBenchmark,
    };

    return {
      headerName,
      headerComponent: HeaderCellRenderer,
      headerComponentParams,
      field: `value.${index}`,
      valueFormatter: getDefaultValueFormat,
      cellRenderer: ValueCellRenderer,
      cellClass: getDefaultCellClass,
    };
  });

  return [labelColumn, ...columns];
};

/**
 * Wraps the labelColumn in a group column for use alongside other group columns.
 *
 * If this isn't done, the spacing will be wrong for the other column headings, because the
 * labelColumn will only take up a single cell of space. This function makes it take up two cells of space.
 */
export function getGroupColumnLabel<T>(labelColumn: ColDef<T>): ColGroupDef<T> {
  return {
    headerName: '',
    children: [labelColumn],
  };
}

const labelCellClassFunc: CellClassFunc = ({ data }) =>
  compact([
    isReturnsGridRow(data) ? BOLD_CLASS : undefined,
    getSpecialClass(data.label),
    data.isMetadata ? ITALIC_CLASS : undefined,
    LEFT_ALIGN_CLASS,
  ]);
const labelValueGetter: ValueGetterFunc = ({ data }) => (data.date ? data.date : data.label);

// This helper is used in too many places to properly type it at the moment.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function createLabelColumn<T = any>({ requests }: CreateLabelColumnProps): ColDef<T> {
  const frequency = requests?.[0]?.frequency;

  return {
    suppressHeaderMenuButton: true,
    suppressMovable: true,
    minWidth: 100,
    wrapHeaderText: true,
    suppressSizeToFit: true,
    valueGetter: labelValueGetter,
    valueFormatter: ({ data, value }) => (get(data, 'date') ? Dates.toDDMMMYYYY(value, frequency) : value),
    cellClass: labelCellClassFunc,
  };
}
